import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaTruck,
  FaPlus,
  FaSave,
  FaTrash,
  FaEdit,
  FaHome,
  FaArrowUp,
  FaArrowDown,
  FaTimesCircle,
  FaList,
} from "react-icons/fa";
import styles from "./MaintainCaptiveVehicle.module.css";
import FloatingActions from "../../components/FloatingActions";

const emptyForm = {
  P_NUMBER: "",
  P_DOCNUMBER: "",
  P_TRUCK: "",
  P_TKNO: "",
  P_DRIVERNAME: "NA",
  P_DriverMobile: "",
  P_entity_code: "",
  P_DIV: "",
  P_DivName: "",
  P_ITEM_CATG: "",
  P_ITEM_CD: "",
  P_Item_name: "",
  P_Party_Code: "",
  P_Party_name: "",
  P_TP_CD: "",
  p_Trans_name: "",
  P_VehTypeCode: "",
  P_VehTypeName: "",
  P_Weigh_Type: "",
  P_Weigh_Type_Name: "",
  p_load_from: "",
  p_load_point: "",
  p_load_to: "",
  p_unload_point: "",
  P_FIRSTWBNO: "",
  P_FIRSTWT: 1,
  P_STATUS: 0,
  P_FLAG: 0,
  P_LoadUnloadRemark: "",
  is_group_vehicle: "N",
  p_parent_truck: "",
};

const getPNumber = (row) => row?.P_NUMBER ?? row?.p_number ?? row?.P_Number ?? "";

// Prefer UHF5 → UHF4 → UHF3 → UHF2 → UHF1
const pickUhfToken = (row) => {
  const candidates = [row?.UHF5, row?.UHF4, row?.UHF3, row?.UHF2, row?.UHF1];
  for (const v of candidates) {
    const token = String(v || "").trim();
    if (token) return token;
  }
  return "";
};

const yesNoOptions = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(14,165,233,0.14)" : "none",
    fontSize: "0.94rem",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: "12px", overflow: "hidden" }),
  option: (base, state) => ({ ...base, fontSize: "0.9rem", fontWeight: state.isSelected ? 800 : 500 }),
};

const MaintainCaptiveVehicle = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const userCode = user?.emp_id || user?.id || user?.user_code || "ADMIN";
  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const vehicleNoRef = useRef(null);
  const uhfSearchTimerRef = useRef(null);
  const uhfCopySearchTimerRef = useRef(null);

  // phase: select (top only) | action (Modify / Add as New / Cancel) | form
  const [phase, setPhase] = useState("select");
  const [mode, setMode] = useState("new"); // new | edit
  const [pendingPNumber, setPendingPNumber] = useState("");
  const [selectedPNumber, setSelectedPNumber] = useState("");
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [copySearch, setCopySearch] = useState("");
  const [uhfCopyVehicles, setUhfCopyVehicles] = useState([]);
  const [uhfCopyLoading, setUhfCopyLoading] = useState(false);

  const [vehicles, setVehicles] = useState([]);
  const [lookups, setLookups] = useState({});
  const [form, setForm] = useState(emptyForm);

  const [uhfOptions, setUhfOptions] = useState([]);
  const [uhfSearching, setUhfSearching] = useState(false);
  const [showUhfDropdown, setShowUhfDropdown] = useState(false);
  const [itemCategories, setItemCategories] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);
  const [partyOptions, setPartyOptions] = useState([]);
  const [loadPlaceOptions, setLoadPlaceOptions] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });

  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const option = (code, name, extra = {}) => ({
    value: code || "",
    label: `${code || "-"} — ${name || "-"}`,
    code,
    name,
    ...extra,
  });

  const makeOptions = (arr = []) =>
    arr.map((r) =>
      option(r.code, r.name, {
        extra_code: r.extra_code,
        extra_name: r.extra_name,
        item_catg: r.item_catg,
        item_catg_name: r.item_catg_name,
        party_code: r.party_code,
        party_address: r.party_address,
        P_entity_code: r.P_entity_code,
      })
    );

  const makePartyOptions = (arr = []) =>
    arr.map((r) => {
      const code = String(r.code || "").trim();
      const name = String(r.name || "").trim();
      const address = String(r.party_address || "").trim();
      const label = [code, name, address].filter(Boolean).join(" — ");
      return option(code, name, {
        party_code: r.party_code,
        party_address: address,
        label,
      });
    });

  const vehicleOptions = useMemo(() => {
    return vehicles.map((r) => ({
      value: String(getPNumber(r)),
      label: `${r.P_TRUCK || "-"} — ${r.P_TKNO || "-"} — ${r.P_Item_name || "-"}`,
      row: r,
    }));
  }, [vehicles]);

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const fetchItemsByCategory = async (itemCatg) => {
    const catg = String(itemCatg || "").trim();
    if (!catg) {
      setCategoryItems([]);
      return;
    }

    try {
      const res = await axios.post("/api/wbauto/captive/itemsByCategory", {
        emp_id,
        token,
        itemCatg: catg,
      });
      setCategoryItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setCategoryItems([]);
      showPopup(err?.response?.data?.message || "Failed to load items.", "Error");
    }
  };

  const fetchDivisionsByEntity = async (entityCode) => {
    const code = String(entityCode || "").trim();
    if (!code) {
      setDivisionOptions([]);
      return;
    }

    try {
      const res = await axios.post("/api/wbauto/captive/divisionsByEntity", {
        emp_id,
        token,
        entityCode: code,
      });
      setDivisionOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setDivisionOptions([]);
      showPopup(err?.response?.data?.message || "Failed to load divisions.", "Error");
    }
  };

  const fetchLookups = async () => {
    const [lookupsRes, categoriesRes, partiesRes, entitiesRes, loadPlacesRes] =
      await Promise.all([
        axios.post("/api/wbauto/captive/lookups", { emp_id, token }),
        axios.post("/api/wbauto/captive/itemCategories", { emp_id, token }),
        axios.post("/api/wbauto/captive/parties", { emp_id, token }),
        axios.post("/api/wbauto/captive/entities", { emp_id, token }),
        axios.post("/api/wbauto/captive/loadPlaces", { emp_id, token }),
      ]);
    setLookups(lookupsRes.data || {});
    setItemCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    setPartyOptions(Array.isArray(partiesRes.data) ? partiesRes.data : []);
    setEntityOptions(Array.isArray(entitiesRes.data) ? entitiesRes.data : []);
    setLoadPlaceOptions(Array.isArray(loadPlacesRes.data) ? loadPlacesRes.data : []);
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.post("/api/wbauto/captive/list", { emp_id, token, search: "" });
      setVehicles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showPopup(err?.response?.data?.message || "Failed to load captive vehicles.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const loadDetailData = async (pNumber) => {
    const res = await axios.post("/api/wbauto/captive/detail", {
      emp_id,
      token,
      pNumber: Number(pNumber),
    });
    const data = res.data || {};

    if (data.P_ITEM_CATG) {
      await fetchItemsByCategory(data.P_ITEM_CATG);
    } else {
      setCategoryItems([]);
    }
    if (data.P_entity_code) {
      await fetchDivisionsByEntity(data.P_entity_code);
    } else {
      setDivisionOptions([]);
    }

    return data;
  };

  const resetToSelect = () => {
    setPhase("select");
    setMode("new");
    setPendingPNumber("");
    setSelectedPNumber("");
    setForm(emptyForm);
    setShowCopyPicker(false);
    setUhfOptions([]);
    setShowUhfDropdown(false);
    setCategoryItems([]);
    setDivisionOptions([]);
  };

  const handleTopVehicleChange = (opt) => {
    const pNumber = opt?.value || "";
    setSelectedPNumber(pNumber);
    if (!pNumber) {
      resetToSelect();
      return;
    }
    setPendingPNumber(pNumber);
    setPhase("action");
    setForm(emptyForm);
    setShowCopyPicker(false);
  };

  const handleStartAddNew = () => {
    setMode("new");
    setPhase("form");
    setPendingPNumber("");
    setSelectedPNumber("");
    setForm(emptyForm);
    setShowCopyPicker(false);
    setUhfOptions([]);
    setShowUhfDropdown(false);
    setCategoryItems([]);
    setDivisionOptions([]);
    setTimeout(() => vehicleNoRef.current?.focus(), 80);
  };

  const handleModify = async () => {
    if (!pendingPNumber) {
      showPopup("Select a vehicle first.", "Validation");
      return;
    }

    setLoading(true);
    try {
      const data = await loadDetailData(pendingPNumber);
      setForm({ ...emptyForm, ...data, is_group_vehicle: "N", p_parent_truck: "" });
      setMode("edit");
      setPhase("form");
      setShowCopyPicker(false);
      setUhfOptions([]);
      setShowUhfDropdown(false);
      if (data?.P_TRUCK) {
        await lookupVehicleGroup(data.P_TRUCK);
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Failed to load detail.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsNewFromSelect = async () => {
    if (!pendingPNumber) {
      showPopup("Select a vehicle first.", "Validation");
      return;
    }

    setLoading(true);
    try {
      const data = await loadDetailData(pendingPNumber);
      setForm({
        ...emptyForm,
        ...data,
        P_NUMBER: "",
        P_DOCNUMBER: "",
        P_TRUCK: "",
        P_TKNO: "",
        P_FIRSTWT: 1,
        P_STATUS: 0,
        is_group_vehicle: "N",
        p_parent_truck: "",
      });
      setMode("new");
      setPhase("form");
      setSelectedPNumber("");
      setShowCopyPicker(false);
      setUhfOptions([]);
      setShowUhfDropdown(false);
      setTimeout(() => vehicleNoRef.current?.focus(), 80);
    } catch (err) {
      showPopup(err?.response?.data?.message || "Failed to load detail.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFromExisting = async (row) => {
    if (!row) return;

    const truck = String(row?.VehicleNo || "").trim().toUpperCase();
    if (!truck) {
      showPopup("Selected UHF record has no Vehicle No.", "Validation");
      return;
    }

    const rfid = pickUhfToken(row);
    setForm((prev) => ({
      ...prev,
      P_TRUCK: truck,
      P_TKNO: rfid || prev.P_TKNO,
    }));
    setShowCopyPicker(false);
    setCopySearch("");
    setToast(rfid ? "✅ Vehicle & RFID filled from UHF tag" : "✅ Vehicle selected from UHF");
    await lookupVehicleGroup(truck);
  };

  const fetchUhfCopyList = async (search = "") => {
    setUhfCopyLoading(true);
    try {
      const res = await axios.post("/api/wbauto/captive/uhfVehicleList", {
        emp_id,
        token,
        search: String(search || "").trim(),
      });
      setUhfCopyVehicles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setUhfCopyVehicles([]);
      showPopup(
        err?.response?.data?.message || "Failed to load UHF vehicles.",
        "Error"
      );
    } finally {
      setUhfCopyLoading(false);
    }
  };

  const openCopyPicker = async () => {
    setShowCopyPicker(true);
    setCopySearch("");
    await fetchUhfCopyList("");
  };

  const handleCopySearchChange = (value) => {
    setCopySearch(value);
    if (uhfCopySearchTimerRef.current) clearTimeout(uhfCopySearchTimerRef.current);
    uhfCopySearchTimerRef.current = setTimeout(() => {
      fetchUhfCopyList(value);
    }, 250);
  };

  const searchVehicleByUHF = async (vehicleNo) => {
    const q = String(vehicleNo || "").trim().toUpperCase();
    if (q.length < 1) {
      setUhfOptions([]);
      setShowUhfDropdown(false);
      return;
    }

    setUhfSearching(true);
    try {
      const res = await axios.post("/api/wbauto/captive/searchByUHF", {
        emp_id,
        token,
        vehicleNo: q,
      });
      const rows = Array.isArray(res.data) ? res.data : [];

      if (rows.length === 0) {
        setUhfOptions([]);
        setShowUhfDropdown(false);
        // Only notify when a full-ish vehicle no was typed (avoid spam while typing)
        if (q.length >= 9) {
          showPopup("No record found", "Notice");
        }
        return;
      }

      setUhfOptions(rows);
      setShowUhfDropdown(true);
    } catch (err) {
      setUhfOptions([]);
      setShowUhfDropdown(false);
      showPopup(
        err?.response?.data?.message || err?.message || "Vehicle search failed.",
        "Error"
      );
    } finally {
      setUhfSearching(false);
    }
  };

  const handleVehicleNoChange = (value) => {
    const next = String(value || "").toUpperCase().replace(/\s+/g, "");
    setField("P_TRUCK", next);

    if (uhfSearchTimerRef.current) clearTimeout(uhfSearchTimerRef.current);

    if (next.length < 1) {
      setUhfOptions([]);
      setShowUhfDropdown(false);
      return;
    }

    uhfSearchTimerRef.current = setTimeout(() => {
      searchVehicleByUHF(next);
    }, 250);
  };

  const lookupVehicleGroup = async (truckNo) => {
    const truck = String(truckNo || "").trim().toUpperCase();
    if (!truck) {
      setForm((p) => ({
        ...p,
        is_group_vehicle: "N",
        p_parent_truck: "",
      }));
      return;
    }

    try {
      const res = await axios.post("/api/wbauto/captive/vehicleGroupByTruck", {
        emp_id,
        token,
        truck,
      });
      const row = res.data;
      const parent = String(row?.p_parent_truck || "").trim().toUpperCase();
      if (parent) {
        setForm((p) => ({
          ...p,
          is_group_vehicle: "Y",
          p_parent_truck: parent,
        }));
      } else {
        setForm((p) => ({
          ...p,
          is_group_vehicle: "N",
          p_parent_truck: "",
        }));
      }
    } catch (err) {
      // keep current form values if lookup fails
      console.error("vehicle group lookup failed:", err);
    }
  };

  const handleSelectUhfVehicle = (row) => {
    const truck = String(row?.VehicleNo || "").trim().toUpperCase();
    const rfid = pickUhfToken(row);

    setForm((prev) => ({
      ...prev,
      P_TRUCK: truck,
      P_TKNO: rfid || prev.P_TKNO,
    }));
    setUhfOptions([]);
    setShowUhfDropdown(false);
    setToast(rfid ? "✅ Vehicle & RFID filled from UHF tag" : "✅ Vehicle selected");
    lookupVehicleGroup(truck);
  };

  const validate = () => {
    if (!form.P_TRUCK) return "Vehicle No is required.";
    if (!form.P_TKNO) return "RFID / Token No is required.";
    if (form.is_group_vehicle === "Y" && !String(form.p_parent_truck || "").trim()) {
      return "Parent Vehicle No is required for group vehicle.";
    }
    if (!form.P_entity_code) return "Entity is required.";
    if (!form.P_DIV) return "Division is required.";
    if (!form.P_ITEM_CD || !form.P_ITEM_CATG) return "Item is required.";
    if (!form.P_Party_Code) return "Party is required.";
    if (!form.P_TP_CD) return "Transporter is required.";
    if (!form.P_VehTypeCode) return "Vehicle Type is required.";
    if (!form.P_Weigh_Type) return "Weigh Type is required.";
    if (!form.p_load_from) return "Load From is required.";
    if (!form.p_load_to) return "Load To is required.";
    return "";
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) {
      showPopup(msg, "Validation");
      return;
    }

    const createNew = mode === "new";

    if (!createNew && !form.P_NUMBER) {
      showPopup("Select a vehicle first to update.", "Validation");
      return;
    }

    setSaving(true);
    try {
      const url = createNew
        ? "/api/wbauto/captive/create"
        : "/api/wbauto/captive/update";

      const payload = createNew
        ? {
            ...form,
            P_NUMBER: "",
            P_DOCNUMBER: "",
            emp_id,
            token,
            userCode,
          }
        : {
            ...form,
            emp_id,
            token,
            userCode,
          };

      const res = await axios.post(url, payload);

      if (res.data?.success) {
        try {
          await axios.post("/api/wbauto/captive/saveVehicleGroup", {
            emp_id,
            token,
            truck: form.P_TRUCK,
            parentTruck: form.p_parent_truck,
            isGroup: form.is_group_vehicle,
          });
        } catch (groupErr) {
          showPopup(
            groupErr?.response?.data?.message ||
              "Saved captive record, but vehicle group save failed.",
            "Warning"
          );
        }

        setToast(`✅ ${res.data.message}`);
        await fetchList();
        resetToSelect();
      } else {
        showPopup(res.data?.message || "Save failed.", "Error");
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Save failed.", "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.P_NUMBER) {
      showPopup("Select a record first.", "Validation");
      return;
    }

    const ok = window.confirm(`Delete captive vehicle ${form.P_TRUCK}?`);
    if (!ok) return;

    setSaving(true);
    try {
      const res = await axios.post("/api/wbauto/captive/delete", {
        emp_id,
        token,
        pNumber: Number(form.P_NUMBER),
      });

      if (res.data?.success) {
        setToast(`✅ ${res.data.message}`);
        await fetchList();
        resetToSelect();
      } else {
        showPopup(res.data?.message || "Delete failed.", "Error");
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Delete failed.", "Error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    fetchLookups();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    return () => {
      if (uhfSearchTimerRef.current) clearTimeout(uhfSearchTimerRef.current);
      if (uhfCopySearchTimerRef.current) clearTimeout(uhfCopySearchTimerRef.current);
    };
  }, []);

  // Server-side search already applied — show API rows as-is
  const filteredCopyVehicles = uhfCopyVehicles;

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaTruck className={styles.loginIcon} />
          <p>You must be logged in.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const items = makeOptions(categoryItems);
  const itemCategoryOptions = makeOptions(itemCategories);
  const parties = makePartyOptions(partyOptions);
  const loadPlaces = (loadPlaceOptions.length
    ? loadPlaceOptions
    : lookups.loadPlaces || []
  ).map((r) => {
    const placeName = String(r.code || r.PlaceName || r.name || "").trim();
    const placeType = String(r.PlaceType || "").trim();
    return {
      value: placeName,
      label: placeType ? `${placeName} — ${placeType}` : placeName,
      code: placeName, // PlaceName → p_load_from / p_load_to
      name: placeName,
      PlaceType: placeType, // PlaceType → p_load_point / p_unload_point
    };
  });
  const entities = makeOptions(entityOptions);
  const divisions = makeOptions(divisionOptions);
  const transporters = makeOptions(lookups.transporters);
  const vehicleTypes = makeOptions(lookups.vehicleTypes);
  const weighTypes = makeOptions(lookups.weighTypes);
  const showForm = phase === "form";

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {showCopyPicker && (
        <div
          className={styles.modalBackdrop}
          onClick={() => {
            if (!uhfCopyLoading) {
              setShowCopyPicker(false);
              setCopySearch("");
            }
          }}
        >
          <div
            className={`${styles.modalSheet} ${styles.copyModalSheet}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.copyModalHeader}>
              <h4>Fill from Existing Vehicle</h4>
              <button
                type="button"
                className={styles.copyModalClose}
                onClick={() => {
                  setShowCopyPicker(false);
                  setCopySearch("");
                }}
                disabled={uhfCopyLoading}
              >
                <FaTimesCircle />
              </button>
            </div>

            <input
              className={styles.input}
              type="text"
              value={copySearch}
              placeholder="Search vehicle / RFID"
              onChange={(e) => handleCopySearchChange(e.target.value)}
              autoFocus
            />

            <div className={styles.copyList}>
              {uhfCopyLoading ? (
                <div className={styles.copyEmpty}>Searching UHF vehicles...</div>
              ) : filteredCopyVehicles.length === 0 ? (
                <div className={styles.copyEmpty}>No vehicles found.</div>
              ) : (
                filteredCopyVehicles.map((r) => {
                  const key = `${r.Code || ""}-${r.VehicleNo || ""}`;
                  const rfid = pickUhfToken(r);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={styles.copyListItem}
                      disabled={uhfCopyLoading}
                      onClick={() => handleCopyFromExisting(r)}
                    >
                      <strong>{r.VehicleNo || "-"}</strong>
                      <span>RFID: {rfid || "-"}</span>
                      <em>Code: {r.Code ?? "-"}</em>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaTruck />
        </div>
        <div>
          <h2>Maintain Captive Vehicle</h2>
          <p>Select a vehicle to modify, or add a new captive entry.</p>
        </div>
      </div>

      {!showForm && (
        <div className={styles.panel}>
          <div className={styles.sectionTitle}>
            <FaEdit /> Select Vehicle
          </div>

          <Select
            styles={selectStyles}
            isSearchable
            isClearable
            isDisabled={loading}
            placeholder={loading ? "Loading..." : "Search Vehicle / RFID / Item"}
            value={vehicleOptions.find((o) => o.value === selectedPNumber) || null}
            options={vehicleOptions}
            filterOption={(option, input) => {
              const q = input.toUpperCase();
              const r = option.data.row;
              return (
                String(r.P_TRUCK || "")
                  .toUpperCase()
                  .includes(q) ||
                String(r.P_TKNO || "")
                  .toUpperCase()
                  .includes(q) ||
                String(r.P_Item_name || "")
                  .toUpperCase()
                  .includes(q) ||
                String(r.P_DOCNUMBER || "")
                  .toUpperCase()
                  .includes(q)
              );
            }}
            onChange={handleTopVehicleChange}
          />

          <div className={styles.resultMeta}>{vehicles.length} records loaded</div>

          {phase === "select" && (
            <div className={styles.topButtons}>
              <button className={styles.newBtn} onClick={handleStartAddNew} disabled={loading}>
                <FaPlus /> Add New
              </button>
              <button className={styles.cancelBtn} onClick={() => navigate("/dashboard")}>
                <FaTimesCircle /> Cancel
              </button>
            </div>
          )}

          {phase === "action" && (
            <div className={styles.actionChoices}>
              <div className={styles.modeHint}>
                Vehicle selected — choose <b>Modify</b> or <b>Add as New</b>.
              </div>
              <button className={styles.saveBtn} onClick={handleModify} disabled={loading}>
                <FaEdit /> {loading ? "Loading..." : "Modify"}
              </button>
              <button
                className={styles.saveAsNewBtn}
                onClick={handleAddAsNewFromSelect}
                disabled={loading}
              >
                <FaPlus /> {loading ? "Loading..." : "Add as New"}
              </button>
              <button className={styles.cancelBtn} onClick={resetToSelect} disabled={loading}>
                <FaTimesCircle /> Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <>
          <Section title="Vehicle & Token">
            {mode === "edit" && (
              <>
                <Input label="P Number" value={form.P_NUMBER} disabled />
                <Input label="Doc Number" value={form.P_DOCNUMBER} disabled />
              </>
            )}

            <div className={styles.formItem}>
              <label>
                Vehicle No <span>*</span>
              </label>
              <div className={styles.vehicleNoRow}>
                <input
                  ref={vehicleNoRef}
                  className={styles.input}
                  type="text"
                  value={form.P_TRUCK ?? ""}
                  placeholder="Type vehicle no"
                  onChange={(e) => handleVehicleNoChange(e.target.value)}
                  onBlur={(e) => {
                    setTimeout(() => setShowUhfDropdown(false), 150);
                    lookupVehicleGroup(e.target.value);
                  }}
                  autoComplete="off"
                />
                {mode === "new" && (
                  <button
                    type="button"
                    className={styles.pickVehicleBtn}
                    onClick={openCopyPicker}
                    title="Fill from existing vehicle (UHF)"
                    disabled={uhfCopyLoading}
                  >
                    <FaList />
                  </button>
                )}
              </div>

              {uhfSearching && <div className={styles.uhfHint}>Searching UHF tags...</div>}
              {showUhfDropdown && uhfOptions.length > 0 && (
                <ul className={styles.uhfDropdown}>
                  {uhfOptions.map((row) => (
                    <li
                      key={`${row.Code || ""}-${row.VehicleNo || ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectUhfVehicle(row);
                      }}
                    >
                      <strong>{row.VehicleNo}</strong>
                      <span>RFID: {pickUhfToken(row) || "-"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Input
              label="RFID / Token No"
              value={form.P_TKNO}
              onChange={(v) => setField("P_TKNO", v)}
              required
            />

            <div className={styles.formItem}>
              <label>Group Vehicle</label>
              <Select
                styles={selectStyles}
                isSearchable={false}
                options={yesNoOptions}
                value={
                  yesNoOptions.find((o) => o.value === form.is_group_vehicle) ||
                  yesNoOptions[1]
                }
                onChange={(o) => {
                  const next = o?.value || "N";
                  setForm((p) => ({
                    ...p,
                    is_group_vehicle: next,
                    p_parent_truck: next === "Y" ? p.p_parent_truck : "",
                  }));
                }}
                placeholder="Select Yes / No"
              />
            </div>

            <Input
              label="Parent Vehicle No"
              value={form.p_parent_truck}
              onChange={(v) =>
                setField("p_parent_truck", String(v || "").toUpperCase().replace(/\s+/g, ""))
              }
              required={form.is_group_vehicle === "Y"}
              disabled={form.is_group_vehicle !== "Y"}
            />

            <Input
              label="Driver Name"
              value={form.P_DRIVERNAME}
              onChange={(v) => setField("P_DRIVERNAME", v)}
            />
            <Input
              label="Driver Mobile"
              value={form.P_DriverMobile}
              onChange={(v) => setField("P_DriverMobile", v)}
            />
          </Section>

          <Section title="Company / Division">
            <Lookup
              label="Entity"
              value={form.P_entity_code}
              options={entities}
              onChange={(o) => {
                const entityCode = o?.code || "";
                setForm((p) => ({
                  ...p,
                  P_entity_code: entityCode,
                  P_DIV: "",
                  P_DivName: "",
                }));
                fetchDivisionsByEntity(entityCode);
              }}
            />
            <Lookup
              label="Division"
              value={form.P_DIV}
              options={divisions}
              isDisabled={!form.P_entity_code}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_DIV: o?.code || "",
                  P_DivName: o?.name || "",
                }))
              }
            />
          </Section>

          <Section title="Item / Party / Transporter">
            <Lookup
              label="Item Category"
              value={form.P_ITEM_CATG}
              options={itemCategoryOptions}
              onChange={(o) => {
                const catg = o?.code || "";
                setForm((p) => ({
                  ...p,
                  P_ITEM_CATG: catg,
                  P_ITEM_CD: "",
                  P_Item_name: "",
                }));
                fetchItemsByCategory(catg);
              }}
            />

            <Lookup
              label="Item"
              value={form.P_ITEM_CD}
              options={items}
              isDisabled={!form.P_ITEM_CATG}
              placeholder={form.P_ITEM_CATG ? "Select Item" : "Select Item Category first"}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_ITEM_CD: o?.code || "",
                  P_Item_name: o?.name || "",
                  P_ITEM_CATG: o?.item_catg || p.P_ITEM_CATG || "",
                }))
              }
            />

            <Lookup
              label="Party"
              value={form.P_Party_Code}
              options={parties}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_Party_Code: o?.code || o?.party_code || "",
                  P_Party_name: o?.name || "",
                }))
              }
            />

            <Lookup
              label="Transporter"
              value={form.P_TP_CD}
              options={transporters}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_TP_CD: o?.code || "",
                  p_Trans_name: o?.name || "",
                }))
              }
            />
          </Section>

          <Section title="Movement Details">
            <Lookup
              label="Load From"
              value={form.p_load_from}
              options={loadPlaces}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  p_load_from: o?.code || "", // PlaceName
                  p_load_point: o?.PlaceType || "", // PlaceType
                }))
              }
            />

            <Lookup
              label="Load To"
              value={form.p_load_to}
              options={loadPlaces}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  p_load_to: o?.code || "", // PlaceName
                  p_unload_point: o?.PlaceType || "", // PlaceType
                }))
              }
            />

            <Input
              label="Remark"
              value={form.P_LoadUnloadRemark}
              onChange={(v) => setField("P_LoadUnloadRemark", v)}
            />
          </Section>

          <Section title="Vehicle Type / Weigh Type">
            <Lookup
              label="Vehicle Type"
              value={form.P_VehTypeCode}
              options={vehicleTypes}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_VehTypeCode: o?.code || "",
                  P_VehTypeName: o?.name || "",
                }))
              }
            />
            <Input label="Vehicle Type Name" value={form.P_VehTypeName} disabled />

            <Lookup
              label="Weigh Type"
              value={form.P_Weigh_Type}
              options={weighTypes}
              onChange={(o) =>
                setForm((p) => ({
                  ...p,
                  P_Weigh_Type: o?.code || "",
                  P_Weigh_Type_Name: o?.name || "",
                }))
              }
            />
            <Input label="Weigh Type Name" value={form.P_Weigh_Type_Name} disabled />
          </Section>

          <Section title="Weight / Status">
            <Input
              label="First WB No"
              value={form.P_FIRSTWBNO}
              onChange={(v) => setField("P_FIRSTWBNO", v)}
            />
            <Input
              label="First Weight"
              type="number"
              value={form.P_FIRSTWT}
              onChange={(v) => setField("P_FIRSTWT", v)}
            />
            <Input
              label="Status"
              type="number"
              value={form.P_STATUS}
              onChange={(v) => setField("P_STATUS", v)}
            />
            <Input
              label="Flag"
              type="number"
              value={form.P_FLAG}
              onChange={(v) => setField("P_FLAG", v)}
            />
          </Section>

          <div className={styles.actionPanel}>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              <FaSave />{" "}
              {saving ? "Saving..." : mode === "edit" ? "Update" : "Save New"}
            </button>

            {mode === "edit" && (
              <button
                className={styles.deleteBtn}
                onClick={handleDelete}
                disabled={saving || !form.P_NUMBER}
              >
                <FaTrash /> Delete
              </button>
            )}

            <button className={styles.cancelBtn} onClick={resetToSelect} disabled={saving}>
              <FaTimesCircle /> Cancel
            </button>
          </div>
        </>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className={styles.panel}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.formGrid}>{children}</div>
  </div>
);

const Input = ({ label, value, onChange, type = "text", disabled, required }) => (
  <div className={styles.formItem}>
    <label>
      {label} {required && <span>*</span>}
    </label>
    <input
      className={styles.input}
      type={type}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </div>
);

const Lookup = ({ label, value, options, onChange, isDisabled = false, placeholder }) => {
  const selected = options.find((o) => String(o.code) === String(value)) || null;

  return (
    <div className={styles.formItem}>
      <label>{label}</label>
      <Select
        styles={selectStyles}
        isSearchable
        isClearable
        isDisabled={isDisabled}
        value={selected}
        options={options}
        onChange={onChange}
        placeholder={placeholder || `Select ${label}`}
      />
    </div>
  );
};

export default MaintainCaptiveVehicle;
